import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, MapPin, Activity, CalendarDays, BarChart2,
  Shield, FileBarChart, Clock, PieChart,
  DollarSign, Timer, TrendingUp, AlertTriangle, ClipboardList,
  ChevronRight, Search, LayoutGrid, BarChart3
} from 'lucide-react';
import { Input } from '@/components/ui/input';

const CATEGORIES = [
  {
    id: 'activity',
    label: 'Activity',
    description: 'Productivity, attendance, screenshots & location',
    accent: 'from-sky-500 to-pink-500',
    soft: 'bg-sky-50 text-sky-700 ring-sky-100 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-800/60',
    iconTone: 'bg-sky-50 text-sky-600 dark:bg-sky-950/50 dark:text-sky-400',
    reports: [
      { label: 'Screenshots', icon: Camera, path: '/admin/reports/screenshots', desc: 'Captured screen snapshots per staff' },
      { label: 'GPS Tracking', icon: MapPin, path: '/admin/reports/gps-tracking', desc: 'Location check-in/out history' },
      { label: 'Staff Activities', icon: Activity, path: '/admin/reports/staff-activities', desc: 'Productivity, exceptions & compliance' },
      { label: 'Daily Report', icon: CalendarDays, path: '/admin/reports/daily', desc: 'Attendance & hours per day' },
      { label: 'Monthly Report', icon: BarChart2, path: '/admin/reports/monthly', desc: 'Monthly summary & trends' },
      { label: 'Avg Stats', icon: TrendingUp, path: '/admin/activity-reports', desc: 'Avg break, idle & productive hours' },
    ],
  },
  {
    id: 'usage',
    label: 'Usage & Policy',
    description: 'App usage, websites and policy compliance',
    accent: 'from-indigo-500 to-purple-500',
    soft: 'bg-indigo-50 text-indigo-700 ring-indigo-100 dark:bg-indigo-950/50 dark:text-indigo-300 dark:ring-indigo-800/60',
    iconTone: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400',
    reports: [
      { label: 'Create Policy', icon: Shield, path: '/admin/usage-policy', desc: 'Define allowed & blocked apps/sites' },
      { label: 'Usage Report', icon: FileBarChart, path: '/admin/reports/usage-report', desc: 'Daily usage breakdown by staff' },
      { label: 'Usage Timeline', icon: Clock, path: '/admin/reports/usage-timeline', desc: 'Chronological app usage history' },
      { label: 'Apps Summary', icon: PieChart, path: '/admin/reports/apps-summary', desc: 'Top apps with usage donut chart' },
    ],
  },
  {
    id: 'payroll',
    label: 'Payroll',
    description: 'Timesheets, salary, overtime, leave & audit',
    accent: 'from-slate-600 to-slate-800',
    soft: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700',
    iconTone: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    reports: [
      { label: 'Timesheet', icon: Timer, path: '/admin/reports/payroll/timesheet', desc: 'Hours worked, breaks & overtime' },
      { label: 'Salary Calculation', icon: DollarSign, path: '/admin/reports/payroll/salary', desc: 'Net pay with deductions & bonuses' },
      { label: 'Overtime Report', icon: TrendingUp, path: '/admin/reports/payroll/overtime', desc: 'OT hours, rates and total cost' },
      { label: 'Leave & Absence', icon: AlertTriangle, path: '/admin/reports/payroll/leave', desc: 'Absences, leave types & salary impact' },
      { label: 'Payroll Audit', icon: ClipboardList, path: '/admin/reports/payroll/audit', desc: 'Manual adjustments & override log' },
    ],
  },
];

const TABS = [
  { id: 'all', label: 'All reports' },
  ...CATEGORIES.map((c) => ({ id: c.id, label: c.label })),
];

export default function ReportsHubPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [query, setQuery] = useState('');

  const totalReports = CATEGORIES.reduce((n, c) => n + c.reports.length, 0);

  const filteredCategories = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CATEGORIES
      .filter((cat) => activeTab === 'all' || cat.id === activeTab)
      .map((cat) => ({
        ...cat,
        reports: cat.reports.filter((r) => {
          if (!q) return true;
          return (
            r.label.toLowerCase().includes(q) ||
            r.desc.toLowerCase().includes(q) ||
            cat.label.toLowerCase().includes(q)
          );
        }),
      }))
      .filter((cat) => cat.reports.length > 0);
  }, [activeTab, query]);

  const visibleCount = filteredCategories.reduce((n, c) => n + c.reports.length, 0);

  return (
    <div className="relative pb-8" data-testid="reports-hub-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative mx-auto max-w-6xl space-y-5 md:space-y-6">
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Reports</h1>
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold backdrop-blur">
                    <LayoutGrid className="h-3.5 w-3.5" />
                    {totalReports} available
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-indigo-50/90">
                  Browse activity, usage, and payroll reports
                </p>
              </div>
              <div className="relative w-full sm:max-w-xs">
                <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-indigo-100/80" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search reports…"
                  className="h-11 rounded-2xl border-0 bg-white/15 pl-10 text-white placeholder:text-indigo-100/70 ring-1 ring-white/25 backdrop-blur focus-visible:ring-white/40"
                  data-testid="reports-search"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-6">
            <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">
              <BarChart3 className="h-3 w-3 text-purple-600" />
              {activeTab === 'all' ? 'All categories' : CATEGORIES.find((c) => c.id === activeTab)?.label}
            </span>
            {query && (
              <span className="text-xs text-slate-500">
                {visibleCount} result{visibleCount === 1 ? '' : 's'} for “{query}”
              </span>
            )}
          </div>
        </section>

        <section className="att-fade-up grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4" style={{ animationDelay: '60ms' }}>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveTab(cat.id)}
              className={`group relative overflow-hidden rounded-2xl border bg-white p-4 text-left shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg ${
                activeTab === cat.id ? 'border-purple-300 ring-1 ring-purple-200' : 'border-slate-200/80'
              }`}
            >
              <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${cat.accent} opacity-10 blur-xl`} />
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{cat.label}</p>
                <p className="mt-2 text-3xl font-bold tabular-nums text-slate-900">{cat.reports.length}</p>
                <p className="mt-1 text-xs text-slate-500 line-clamp-1">{cat.description}</p>
              </div>
            </button>
          ))}
        </section>

        <div className="att-fade-up overflow-x-auto" style={{ animationDelay: '100ms' }}>
          <div className="inline-flex min-w-full gap-1 rounded-2xl bg-slate-100/90 p-1.5 dark:bg-slate-900/80 sm:min-w-0">
            {TABS.map((tab) => {
              const count =
                tab.id === 'all'
                  ? totalReports
                  : CATEGORIES.find((c) => c.id === tab.id)?.reports.length || 0;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition ${
                    active
                      ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-50 dark:ring-slate-700'
                      : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                  data-testid={`reports-tab-${tab.id}`}
                >
                  <span className="whitespace-nowrap">{tab.label}</span>
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[11px] tabular-nums ${
                      active
                        ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        : 'bg-white/60 text-slate-400 dark:bg-slate-800/60 dark:text-slate-500'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {filteredCategories.length === 0 ? (
          <div className="att-fade-up rounded-3xl border border-dashed border-slate-200 bg-white/80 px-6 py-20 text-center">
            <p className="text-lg font-bold text-slate-900">No reports match “{query}”</p>
            <p className="mt-1 text-sm text-slate-500">Try another keyword or clear the search.</p>
            <button
              type="button"
              onClick={() => { setQuery(''); setActiveTab('all'); }}
              className="mt-5 text-sm font-semibold text-purple-700 hover:underline"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="att-fade-up space-y-6" style={{ animationDelay: '140ms' }}>
            {filteredCategories.map((cat) => (
              <section
                key={cat.id}
                aria-labelledby={`reports-cat-${cat.id}`}
                className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm dark:border-slate-800 dark:bg-[hsl(222_47%_9%)]"
              >
                <div className="flex items-start gap-3 border-b border-slate-100 bg-slate-50/60 px-5 py-4 dark:border-slate-800 dark:bg-slate-900/80">
                  <div className={`mt-0.5 h-10 w-1.5 shrink-0 rounded-full bg-gradient-to-b ${cat.accent}`} aria-hidden />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 id={`reports-cat-${cat.id}`} className="text-lg font-bold text-slate-900 dark:text-slate-50">
                        {cat.label}
                      </h2>
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ${cat.soft}`}>
                        {cat.reports.length}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">{cat.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
                  {cat.reports.map((rep, idx) => {
                    const Icon = rep.icon;
                    return (
                      <button
                        key={rep.path}
                        type="button"
                        onClick={() => navigate(rep.path)}
                        className="group relative flex items-start gap-3 overflow-hidden rounded-2xl border border-slate-200/80 bg-white px-4 py-3.5 text-left shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                        data-testid={`report-card-${rep.label.toLowerCase().replace(/\s+/g, '-')}`}
                        style={{ animationDelay: `${Math.min(idx, 6) * 30}ms` }}
                      >
                        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${cat.iconTone}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-semibold text-slate-900">{rep.label}</p>
                            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-purple-600" />
                          </div>
                          <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-slate-500">
                            {rep.desc}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
